import { Link } from 'react-router-dom';
import { Award, Users, Shield, Clock, ArrowRight } from 'lucide-react';

export default function About() {
  const teamMembers = [
    { name: 'Michael Brennan', role: 'Founder & Lead Technician', experience: '22 years', photo: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?w=400&q=80&auto=format&fit=crop&crop=faces', alt: 'Portrait of a silver-haired tradesman in a button-down shirt' },
    { name: 'David Chen', role: 'Senior G2 Technician', experience: '15 years', photo: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&q=80&auto=format&fit=crop&crop=faces', alt: 'Portrait of a smiling man in his forties' },
    { name: 'Marco Rodriguez', role: 'HVAC Technician', experience: '8 years', photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=400&q=80&auto=format&fit=crop&crop=faces', alt: 'Portrait of a young man with short dark hair' },
    { name: 'James Wilson', role: 'HVAC Technician', experience: '6 years', photo: 'https://images.unsplash.com/photo-1531427186611-ecfd6d936c79?w=400&q=80&auto=format&fit=crop&crop=faces', alt: 'Portrait of a working-class man in casual clothes' },
    { name: 'Sarah Thompson', role: 'Service Technician', experience: '4 years', photo: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=400&q=80&auto=format&fit=crop&crop=faces', alt: 'Portrait of a professional woman with short hair' },
    { name: 'Lisa Park', role: 'Office Administrator', experience: '5 years', photo: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=400&q=80&auto=format&fit=crop&crop=faces', alt: 'Portrait of a friendly woman with long hair' }
  ];

  const credentials = [
    'TSSA Gas Technician 2 License #DEMO-0000',
    'HRAI Member Contractor',
    'WSIB Clearance',
    '$5M Liability Insurance',
    'Manufacturer-certified for Lennox, Carrier, Daikin, Mitsubishi',
    'Better Business Bureau A+ Rating'
  ];

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="bg-[var(--color-slate-blue)] text-white py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl md:text-5xl font-bold mb-6 text-white">About Northbridge HVAC</h1>
          <p className="text-xl text-gray-100 max-w-3xl">
            Eight years. Six techs. One promise: we leave the house better than we found it.
          </p>
        </div>
      </section>

      {/* Founder Story */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold mb-8 text-[var(--color-text-dark)]">Our Story</h2>
            <div className="prose prose-lg text-gray-700 space-y-4">
              <p>
                Northbridge HVAC was founded in 2018 by Michael Brennan, a 22-year HVAC veteran who'd spent the previous decade running service crews for one of the GTA's largest mechanical contractors. He started Northbridge for a simple reason: he was tired of watching his crews get pushed to upsell homeowners on equipment they didn't need.
              </p>
              <p>
                Eight years later we're a six-person shop serving York Region and north Toronto. Every technician is TSSA-licensed. Every quote is written down before work begins. We do residential install, service, and indoor air quality — heat pumps, high-efficiency furnaces, central AC, ductless splits, HRVs, and whole-home filtration.
              </p>
              <p>
                We're not the biggest HVAC company in York Region and we don't want to be. We want to be the one your neighbour tells you to call.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-16 bg-[var(--color-neutral-bg)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-4 text-center text-[var(--color-text-dark)]">Our Team</h2>
          <p className="text-xl text-center mb-12 text-gray-600 max-w-3xl mx-auto">
            Six licensed professionals committed to doing the job right the first time
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md">
                <img
                  src={member.photo}
                  alt={member.alt}
                  width={160}
                  height={160}
                  loading="lazy"
                  className="w-20 h-20 rounded-full mb-4 mx-auto object-cover"
                />
                <h3 className="text-xl font-bold text-center mb-2 text-[var(--color-text-dark)]">
                  {member.name}
                </h3>
                <p className="text-center text-gray-600 mb-1">{member.role}</p>
                <p className="text-center text-sm text-gray-500">{member.experience} experience</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-16 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-12 text-center text-[var(--color-text-dark)]">
            What Sets Us Apart
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="bg-[var(--color-warm-amber)] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield size={28} />
              </div>
              <h3 className="text-lg font-bold mb-2 text-[var(--color-text-dark)]">Licensed & Insured</h3>
              <p className="text-gray-600">
                Every technician is TSSA-licensed. Full WSIB coverage and $5M liability insurance.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-[var(--color-warm-amber)] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users size={28} />
              </div>
              <h3 className="text-lg font-bold mb-2 text-[var(--color-text-dark)]">Owner-Operated</h3>
              <p className="text-gray-600">
                You're not dealing with a franchise. Michael answers the phone and stands behind every job.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-[var(--color-warm-amber)] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Clock size={28} />
              </div>
              <h3 className="text-lg font-bold mb-2 text-[var(--color-text-dark)]">Transparent Pricing</h3>
              <p className="text-gray-600">
                Flat-rate pricing on most jobs. You know the cost before we start work.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-[var(--color-warm-amber)] text-white w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award size={28} />
              </div>
              <h3 className="text-lg font-bold mb-2 text-[var(--color-text-dark)]">Quality Guarantee</h3>
              <p className="text-gray-600">
                2-year labor warranty on installations. We stand behind our work.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Credentials Section */}
      <section className="py-16 bg-[var(--color-neutral-bg)]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold mb-8 text-center text-[var(--color-text-dark)]">
            Credentials & Certifications
          </h2>
          <div className="max-w-3xl mx-auto">
            <ul className="space-y-3">
              {credentials.map((credential, index) => (
                <li key={index} className="flex items-start gap-3 bg-white p-4 rounded-lg">
                  <Shield size={20} className="text-[var(--color-slate-blue)] mt-1 flex-shrink-0" />
                  <span className="text-gray-700">{credential}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Fictional Disclosure Block */}
      <section className="py-12 bg-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="bg-white border-2 border-gray-300 rounded-lg p-8 max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-4 text-gray-700">About this site</h2>
            <p className="text-gray-600 leading-relaxed">
              <strong>Northbridge HVAC Inc.</strong> is a fictional business created by [Platform Name] as a demonstration 
              of the kind of website we build for HVAC contractor clients. The owner, location, testimonials, and 
              credentials shown are illustrative — they are not real. No services or products can be purchased through 
              this site, and any forms submitted are recorded only for our own quality-assurance purposes. If you 
              arrived here looking for a real HVAC contractor in York Region, please use a directory like Google Maps 
              or HomeStars. If you arrived here because you're considering working with [Platform Name], welcome — 
              you can reach us at [Platform Contact].
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[var(--color-slate-blue)] text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 text-white">
            Ready to Experience the Northbridge Difference?
          </h2>
          <p className="text-xl mb-8 text-gray-100 max-w-2xl mx-auto">
            Join hundreds of satisfied homeowners who trust us with their heating and cooling needs.
          </p>
          <Link 
            to="/contact" 
            className="inline-flex items-center gap-2 bg-[var(--color-warm-amber)] text-white px-8 py-4 rounded text-lg font-semibold hover:bg-opacity-90 transition-colors"
          >
            Book Your Service Call
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}